// ═══════════════════════════════════════════════════════════════════════════════
// System message HTML sanitize + plain-text strip + ticker contrast
// ═══════════════════════════════════════════════════════════════════════════════
export const SYSTEM_MESSAGE_IMAGE_LIGHTBOX_ATTR = 'data-sysmsg-lightbox';
export const SYSTEM_MESSAGE_IMAGE_LIGHTBOX_SRC_ATTR = 'data-sysmsg-src';
const ALLOWED_TAGS = new Set([
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'ul',
    'ol',
    'li',
    'a',
    'h2',
    'h3',
    'blockquote',
    'span',
    'img',
]);
const VOID_TAGS = new Set(['br', 'img']);
const CSS_LENGTH_UNITS = 'px|em|rem|%|vw|vh|vmin|vmax|svw|svh|dvw|dvh|lvw|lvh|ch|ex|cap|ic|lh|rlh|cm|mm|in|pt|pc|cqw|cqh|cqmin|cqmax';
const CSS_LENGTH_WITH_UNIT_RE = new RegExp(`^(?:0|(?:\\d+(?:\\.\\d+)?)(?:${CSS_LENGTH_UNITS}))$`, 'i');
/** https or site-relative path (not protocol-relative). */
export const isSafeSystemMessageHref = (href) => {
    const trimmed = href.trim();
    if (trimmed.startsWith('/')) {
        return !trimmed.startsWith('//');
    }
    try {
        const url = new URL(trimmed);
        return url.protocol === 'https:';
    }
    catch {
        return false;
    }
};
/** Image / lightbox URLs must be absolute https (no upload, no relative, no data:). */
export const isSafeSystemMessageImageUrl = (href) => {
    try {
        const url = new URL(href.trim());
        return url.protocol === 'https:';
    }
    catch {
        return false;
    }
};
/**
 * Allowlisted CSS length for img max-width / max-height.
 * Unitless numbers are stored as px; other units (%, rem, vh, …) pass through.
 */
export const sanitizeCssLength = (raw) => {
    const collapsed = raw.trim().replace(/\s+/g, '').toLowerCase();
    if (collapsed === '' || collapsed.startsWith('-')) {
        return null;
    }
    if (/^\d+(?:\.\d+)?$/.test(collapsed)) {
        return `${collapsed}px`;
    }
    if (!CSS_LENGTH_WITH_UNIT_RE.test(collapsed)) {
        return null;
    }
    return collapsed;
};
const escapeAttr = (value) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
/** Decode HTML entities in a raw href attribute (editor / prior sanitize may already encode `&`). */
const decodeHrefOnce = (value) => value
    .replace(/&amp;/gi, '&')
    .replace(/&#0*38;/g, '&')
    .replace(/&#x0*26;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&#x0*27;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
const decodeHrefEntities = (value, remaining = 10) => {
    if (remaining <= 0) {
        return value;
    }
    const next = decodeHrefOnce(value);
    if (next === value) {
        return value;
    }
    return decodeHrefEntities(next, remaining - 1);
};
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Attribute names may contain hyphens; do not use `\\b` (`src` must not match inside `data-sysmsg-src`). */
const readTagAttr = (body, name) => {
    const re = new RegExp(`(?:^|[\\s/])${escapeRegExp(name)}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');
    const match = re.exec(body);
    if (!match) {
        return '';
    }
    return decodeHrefEntities((match[1] ?? match[2] ?? match[3] ?? '').trim());
};
const hasLightboxAttr = (body) => {
    const match = /(?:^|[\s/])data-sysmsg-lightbox(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?(?=\s|\/|$)/i.exec(body);
    if (!match) {
        return false;
    }
    const raw = (match[1] ?? match[2] ?? match[3] ?? '').trim().toLowerCase();
    return raw === '' || raw === '1' || raw === 'true';
};
const readLightboxSrc = (body) => {
    const fromData = readTagAttr(body, SYSTEM_MESSAGE_IMAGE_LIGHTBOX_SRC_ATTR);
    if (fromData) {
        return fromData;
    }
    if (hasLightboxAttr(body)) {
        return readTagAttr(body, 'href');
    }
    return '';
};
const sanitizeMaxBoxStyle = (styleRaw) => {
    const decls = [];
    const seen = new Set();
    decodeHrefEntities(styleRaw)
        .split(';')
        .forEach((part) => {
        const colon = part.indexOf(':');
        if (colon <= 0) {
            return;
        }
        const prop = part.slice(0, colon).trim().toLowerCase();
        if (prop !== 'max-width' && prop !== 'max-height') {
            return;
        }
        if (seen.has(prop)) {
            return;
        }
        const value = sanitizeCssLength(part.slice(colon + 1));
        if (!value) {
            return;
        }
        seen.add(prop);
        decls.push(`${prop}: ${value}`);
    });
    return decls.join('; ');
};
const serializeAnchorOpen = (href) => `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">`;
/** Not an `<a href>` — iOS / PWA treat https links as leave-app (new tab) even without target=_blank. */
const serializeLightboxSpan = (src) => `<span role="button" tabindex="0" ${SYSTEM_MESSAGE_IMAGE_LIGHTBOX_ATTR}="1" ${SYSTEM_MESSAGE_IMAGE_LIGHTBOX_SRC_ATTR}="${escapeAttr(src)}">`;
const serializeImg = (src, alt, style) => {
    const styleAttr = style ? ` style="${escapeAttr(style)}"` : '';
    return `<img src="${escapeAttr(src)}" alt="${escapeAttr(alt)}"${styleAttr} />`;
};
/**
 * Allowlist sanitizer for system message detail HTML.
 * Strips disallowed tags/attrs; forces noopener on links; https or site-relative only.
 * Img / lightbox: https URL only; style limited to max-width / max-height lengths.
 * Lightbox is a `<span role="button">` (rewrites legacy lightbox `<a href>`), never a navigable https link.
 * A normal `<a href>` wrapping `<img>` is kept as a navigation link (https or site-relative).
 * Href is decoded then escaped once so re-sanitize does not turn `&` into `&amp;amp;`.
 */
export const sanitizeSystemMessageHtml = (input, maxLen = 10_000) => {
    const source = input.length > maxLen ? input.slice(0, maxLen) : input;
    const anchorCloseStack = [];
    let result = '';
    let i = 0;
    while (i < source.length) {
        const open = source.indexOf('<', i);
        if (open === -1) {
            result += source.slice(i);
            break;
        }
        result += source.slice(i, open);
        const close = source.indexOf('>', open);
        if (close === -1) {
            break;
        }
        const rawTag = source.slice(open + 1, close).trim();
        i = close + 1;
        if (rawTag.startsWith('!--')) {
            continue;
        }
        const isClosing = rawTag.startsWith('/');
        const body = isClosing ? rawTag.slice(1).trim() : rawTag;
        const nameMatch = /^([a-zA-Z][a-zA-Z0-9]*)/.exec(body);
        if (!nameMatch) {
            continue;
        }
        const tagName = nameMatch[1].toLowerCase();
        if (!ALLOWED_TAGS.has(tagName)) {
            continue;
        }
        if (isClosing) {
            if (VOID_TAGS.has(tagName)) {
                continue;
            }
            if (tagName === 'a') {
                const closeTag = anchorCloseStack.pop() ?? 'a';
                result += `</${closeTag}>`;
                continue;
            }
            result += `</${tagName}>`;
            continue;
        }
        if (tagName === 'br') {
            result += '<br />';
            continue;
        }
        if (tagName === 'img') {
            const src = readTagAttr(body, 'src');
            if (!src || !isSafeSystemMessageImageUrl(src)) {
                continue;
            }
            const alt = readTagAttr(body, 'alt');
            const style = sanitizeMaxBoxStyle(readTagAttr(body, 'style'));
            result += serializeImg(src, alt, style);
            continue;
        }
        if (tagName === 'a') {
            const href = readTagAttr(body, 'href');
            const lightboxSrc = readLightboxSrc(body);
            if (lightboxSrc && isSafeSystemMessageImageUrl(lightboxSrc)) {
                anchorCloseStack.push('span');
                result += serializeLightboxSpan(lightboxSrc);
                continue;
            }
            if (hasLightboxAttr(body)) {
                continue;
            }
            if (!href || !isSafeSystemMessageHref(href)) {
                continue;
            }
            anchorCloseStack.push('a');
            result += serializeAnchorOpen(href);
            continue;
        }
        if (tagName === 'span') {
            const src = readLightboxSrc(body);
            if (src && isSafeSystemMessageImageUrl(src)) {
                result += serializeLightboxSpan(src);
                continue;
            }
            result += '<span>';
            continue;
        }
        result += `<${tagName}>`;
    }
    return result;
};
/** Strip tags for notification body / plain preview. */
export const stripHtmlToPlainText = (html) => html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
export const truncatePlainText = (text, maxLen) => {
    if (text.length <= maxLen) {
        return text;
    }
    return `${text.slice(0, Math.max(0, maxLen - 1)).trimEnd()}…`;
};
const parseHexColor = (hex) => {
    const normalized = hex.trim().replace(/^#/, '');
    if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
        return null;
    }
    return {
        r: Number.parseInt(normalized.slice(0, 2), 16),
        g: Number.parseInt(normalized.slice(2, 4), 16),
        b: Number.parseInt(normalized.slice(4, 6), 16),
    };
};
/** Relative-luminance contrast: dark text on light bg, white on dark bg. */
export const contrastTextColorForBackground = (bgHex) => {
    const rgb = parseHexColor(bgHex);
    if (!rgb) {
        return '#111827';
    }
    const toLinear = (c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    const luminance = 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);
    return luminance > 0.45 ? '#111827' : '#ffffff';
};
export const normalizeHexColor = (input, fallback = '#FEF3C7') => {
    const trimmed = input.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
        return trimmed.toUpperCase();
    }
    if (/^[0-9a-fA-F]{6}$/.test(trimmed)) {
        return `#${trimmed.toUpperCase()}`;
    }
    return fallback;
};
//# sourceMappingURL=systemMessageHtml.js.map